import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { normalizeLoginValues, validateLoginValues } from "../../utils/loginValidation";
import { BrandField } from "../form/BrandField";
import { BrandButton } from "../ui/BrandButton";

const initialValues = {
  username: "",
  password: "",
};

export function LoginForm() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [values, setValues] = useState(initialValues);
  const [touched, setTouched] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const showError = (field) => touched[field] && fieldErrors[field];

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({
      ...current,
      [name]: name === "username" ? value.toLowerCase() : value,
    }));
    setSubmitError("");
    setFieldErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouched((current) => ({ ...current, [name]: true }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const normalized = normalizeLoginValues(values);
    const errors = validateLoginValues(normalized);
    setTouched({
      username: true,
      password: true,
    });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setFieldErrors({});

    try {
      const response = await signIn(normalized);
      navigate(response?.phoneRepair?.shouldShow ? "/phone-repair" : "/home", { replace: true });
    } catch (error) {
      if (error.code === "INVALID_CREDENTIALS") {
        setSubmitError("El usuario o la contraseña no son correctos.");
        return;
      }

      if (Array.isArray(error.details)) {
        const mappedErrors = error.details.reduce((acc, item) => {
          if (item?.path) {
            acc[item.path] = item.message;
          }
          return acc;
        }, {});

        if (Object.keys(mappedErrors).length > 0) {
          setFieldErrors(mappedErrors);
          return;
        }
      }

      setSubmitError(error.message || "No pudimos iniciar sesión.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="grid gap-5">
      {submitError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {submitError}
        </div>
      ) : null}

      <form className="grid gap-4" onSubmit={handleSubmit} noValidate>
        <BrandField
          label="Nombre de usuario"
          name="username"
          value={values.username}
          onChange={handleChange}
          onBlur={handleBlur}
          error={showError("username") || fieldErrors.username}
          autoComplete="username"
          required
          showLabel={false}
          placeholder="Nombre de usuario"
        />

        <BrandField
          label="Contraseña"
          name="password"
          value={values.password}
          onChange={handleChange}
          onBlur={handleBlur}
          error={showError("password") || fieldErrors.password}
          autoComplete="current-password"
          required
          type={showPassword ? "text" : "password"}
          actionLabel={showPassword ? "Ocultar" : "Mostrar"}
          onActionClick={() => setShowPassword((current) => !current)}
          showLabel={false}
          placeholder="Contraseña"
        />

        <BrandButton
          type="submit"
          disabled={isSubmitting}
          variant="primary"
          radius="xl"
          size="xl"
          className="w-full uppercase tracking-[0.12em]"
        >
          {isSubmitting ? "Ingresando..." : "LOGIN"}
        </BrandButton>

        <div className="pt-2 text-center text-sm">
          <div className="flex items-center justify-center">
            <Link
              to="/register"
              className="font-bold text-brand-secondary transition-colors hover:text-brand-primary hover:underline"
            >
              Crear una cuenta
            </Link>
          </div>

          <div className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <Link
              to="/forgot-password"
              className="font-semibold text-brand-accent-light transition-colors hover:text-brand-primary hover:underline"
            >
              ¿Olvidaste tu contraseña?
            </Link>
            <Link
              to="/forgot-username"
              className="font-semibold text-brand-accent-light transition-colors hover:text-brand-primary hover:underline"
            >
              ¿Olvidaste tu nombre de usuario?
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
}
